import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Stack, Button, IconButton, TextField, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Tooltip, Snackbar, InputAdornment, CircularProgress, Menu, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Add, Search, Refresh, FileDownloadOutlined, WarningAmberRounded,
  TrendingUp, TrendingDown, TaskAlt, HourglassEmpty, EditNote, Undo, Balance,
  ChevronRight, Send, VerifiedUser, ThumbUp, OpenInNew, ArrowForward,
} from '@mui/icons-material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
} from 'recharts';
import { financeService } from '../../services/financeService';
import {
  journalWorkspaceService, JournalSummary, JournalTrend, JournalExceptions,
} from '../../services/journalWorkspaceService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import {
  StatCard, TableSurface, headerCellSx, bodyRowSx, TypePill, StatusDot, StatusTone,
  TableEmptyState, ErrorState,
} from '../../components/common/pageKit';

const JOURNAL_TYPES = [
  'General Journal', 'Adjustment', 'Accrual', 'Provision', 'Bank', 'Cash', 'Treasury',
  'Purchase', 'Sales', 'Payroll', 'GST', 'TDS', 'Inventory', 'Production',
  'Depreciation', 'Asset', 'Intercompany', 'Reversal',
];
const SOURCE_TYPES = [
  'MANUAL', 'PURCHASE_INVOICE', 'SALES_INVOICE', 'VENDOR_PAYMENT', 'CUSTOMER_RECEIPT',
  'GOODS_RECEIPT', 'MATERIAL_ISSUE', 'FG_RECEIPT', 'STOCK_ADJUSTMENT', 'EXPENSE',
  'JOURNAL_REVERSAL',
];

// Subtle identity tint per journal-type family — NOT a rainbow; most map to a
// restrained slate, with a few functional accents.
const typeTint = (t?: string | null, accent = '#6366f1'): string => {
  const map: Record<string, string> = {
    Reversal: '#a855f7', Depreciation: '#0891b2', Sales: '#0284c7', Purchase: '#059669',
    Payroll: '#7c3aed', GST: '#d97706', TDS: '#d97706', Treasury: '#0284c7',
  };
  return (t && map[t]) || accent;
};

const toNum = (v: any) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const isBalanced = (dr: number, cr: number) => Math.abs(dr - cr) < 0.01;

// Reversed originals deliberately keep status POSTED (see reverseJournalEntry);
// the reversed state is derived from reversal_entry_id.
const rowState = (r: any): string => (r.reversal_entry_id ? 'REVERSED' : (r.status || 'DRAFT'));

const STATE_TONE: Record<string, StatusTone> = {
  DRAFT: 'warning', SUBMITTED: 'info', PENDING_APPROVAL: 'warning', APPROVED: 'info',
  REJECTED: 'error', POSTED: 'success', REVERSED: 'neutral',
};
const stateLabel = (s: string) => s.replace('_', ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const WINDOWS: ('7D' | '30D' | '90D' | 'FY')[] = ['7D', '30D', '90D', 'FY'];

const JournalControlCenter: React.FC = () => {
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const ct = useChartTokens();

  const [summary, setSummary] = useState<JournalSummary | null>(null);
  const [trend, setTrend] = useState<JournalTrend | null>(null);
  const [trendWindow, setTrendWindow] = useState<'7D' | '30D' | '90D' | 'FY'>('30D');
  const [exceptions, setExceptions] = useState<JournalExceptions | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);

  // filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [journalType, setJournalType] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [accountId, setAccountId] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [attentionOnly, setAttentionOnly] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const line = `1px solid ${c.line}`;

  const loadAnalytics = useCallback(() => {
    journalWorkspaceService.summary().then(setSummary).catch(() => {});
    journalWorkspaceService.exceptions().then(setExceptions).catch(() => {});
  }, []);

  useEffect(() => {
    journalWorkspaceService.trend(trendWindow).then(setTrend).catch(() => {});
  }, [trendWindow]);

  const loadList = useCallback(() => {
    setLoading(true);
    journalWorkspaceService.list({
      search: search || undefined, status: status || undefined,
      journalType: journalType || undefined, sourceType: sourceType || undefined,
      fromDate: fromDate || undefined, toDate: toDate || undefined,
      accountId: accountId ? Number(accountId) : undefined,
      minAmount: minAmount ? Number(minAmount) : undefined,
      maxAmount: maxAmount ? Number(maxAmount) : undefined,
    })
      .then((r) => { setRows(r.journalEntries || []); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load journals'))
      .finally(() => setLoading(false));
  }, [search, status, journalType, sourceType, fromDate, toDate, accountId, minAmount, maxAmount]);

  useEffect(() => {
    financeService.getAccounts({ isActive: true }).then((r: any) => setAccounts(r.accounts || [])).catch(() => {});
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { setPage(0); }, [search, status, journalType, sourceType, fromDate, toDate, accountId, minAmount, maxAmount, attentionOnly]);

  const displayRows = useMemo(() => {
    if (!attentionOnly) return rows;
    return rows.filter((r) => !isBalanced(toNum(r.total_debit), toNum(r.total_credit))
      || r.status === 'SUBMITTED' || r.status === 'PENDING_APPROVAL');
  }, [rows, attentionOnly]);

  const paged = useMemo(
    () => displayRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [displayRows, page, rowsPerPage],
  );

  const clearFilters = () => {
    setSearch(''); setStatus(''); setJournalType(''); setSourceType('');
    setFromDate(''); setToDate(''); setAccountId(''); setMinAmount(''); setMaxAmount(''); setAttentionOnly(false);
  };
  const filtersActive = !!(search || status || journalType || sourceType || fromDate || toDate || accountId || minAmount || maxAmount || attentionOnly);

  const rowAction = async (fn: () => Promise<any>, id: number, msg: string) => {
    setBusyId(id);
    try { await fn(); setToast(msg); loadList(); loadAnalytics(); }
    catch (e: any) { setToast(e.message || 'Action failed'); }
    finally { setBusyId(null); }
  };

  const exportCsv = () => {
    setExportAnchor(null);
    const cols = ['entry_number', 'entry_date', 'journal_type', 'source_type', 'reference', 'description', 'company', 'total_debit', 'total_credit', 'status'];
    const head = ['Journal #', 'Date', 'Type', 'Source', 'Reference', 'Description', 'Company', 'Debit', 'Credit', 'Status'];
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = displayRows.map((r) => cols.map((k) => esc(r[k])).join(',')).join('\n');
    const blob = new Blob([head.join(',') + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `journal-entries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const trendData = trend?.points || [];

  // decision KPIs from real summary
  const kpis = summary ? [
    {
      title: 'Posted This Period', color: '#059669', icon: <TaskAlt />,
      value: formatIndianNumber(summary.postedThisPeriod.amount),
      onClick: () => { setStatus('POSTED'); setAttentionOnly(false); },
      footer: (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {summary.postedThisPeriod.vsPrevPeriodPct >= 0
            ? <TrendingUp sx={{ fontSize: 15, color: c.good }} />
            : <TrendingDown sx={{ fontSize: 15, color: c.bad }} />}
          <Typography variant="caption" sx={{ fontWeight: 700, color: summary.postedThisPeriod.vsPrevPeriodPct >= 0 ? c.good : c.bad }}>
            {summary.postedThisPeriod.vsPrevPeriodPct >= 0 ? '+' : ''}{summary.postedThisPeriod.vsPrevPeriodPct}% vs prev
          </Typography>
        </Stack>
      ),
    },
    {
      title: 'Pending Approval', color: '#d97706', icon: <HourglassEmpty />,
      value: summary.pendingApproval.count,
      onClick: () => { setStatus('PENDING_APPROVAL'); },
      footer: <Typography variant="caption" color="text.secondary">{formatIndianNumber(summary.pendingApproval.amount)} awaiting</Typography>,
    },
    {
      title: 'Requires Attention', color: '#dc2626', icon: <WarningAmberRounded />,
      value: summary.requiresAttention,
      onClick: () => setAttentionOnly(true),
      footer: <Typography variant="caption" color="text.secondary">{summary.unbalanced.count} unbalanced · {exceptions?.summary.total ?? 0} exceptions</Typography>,
    },
    {
      title: 'Drafts', color: '#0284c7', icon: <EditNote />,
      value: summary.drafts.count,
      onClick: () => { setStatus('DRAFT'); },
      footer: <Typography variant="caption" color="text.secondary">{formatIndianNumber(summary.drafts.amount)}</Typography>,
    },
    {
      title: 'Reversed', color: '#a855f7', icon: <Undo />,
      value: summary.reversed.count,
      onClick: () => { setJournalType('Reversal'); },
      footer: <Typography variant="caption" color="text.secondary">{formatIndianNumber(summary.reversed.amount)}</Typography>,
    },
    {
      title: 'Net Debit / Credit', color: summary.netDebitCredit.balanced ? '#4f46e5' : '#dc2626', icon: <Balance />,
      value: formatIndianNumber(summary.netDebitCredit.debit),
      onClick: () => setAttentionOnly(!summary.netDebitCredit.balanced),
      footer: (
        <StatusDot
          label={summary.netDebitCredit.balanced ? 'Balanced' : `Off by ${formatCurrency(Math.abs(summary.netDebitCredit.debit - summary.netDebitCredit.credit))}`}
          tone={summary.netDebitCredit.balanced ? 'success' : 'error'}
        />
      ),
    },
  ] : [];

  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  return (
    <Box sx={{ p: 3, bgcolor: c.bg, minHeight: '100%' }}>
      {/* header + toolbar */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Journal Control Center</Typography>
          <Typography variant="body2" color="text.secondary">Monitor, review and post double-entry transactions across the general ledger.</Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="outlined" startIcon={<FileDownloadOutlined />} onClick={(e) => setExportAnchor(e.currentTarget)}>Export</Button>
          <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
            <MenuItem onClick={exportCsv}>Export current view (CSV)</MenuItem>
          </Menu>
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => { loadList(); loadAnalytics(); }}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/finance/journal/new')}>New Journal Entry</Button>
        </Stack>
      </Stack>

      {/* KPI strip */}
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {(summary ? kpis : Array.from({ length: 6 })).map((k: any, i) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={i}>
            {summary
              ? <StatCard title={k.title} value={k.value} icon={k.icon} color={k.color} footer={k.footer} onClick={k.onClick} />
              : <Paper elevation={0} sx={{ ...panelSx, height: 118, display: 'grid', placeItems: 'center' }}><CircularProgress size={20} /></Paper>}
          </Grid>
        ))}
      </Grid>

      {/* trend + exceptions */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Journal Activity</Typography>
              <ToggleButtonGroup size="small" exclusive value={trendWindow} onChange={(_, v) => v && setTrendWindow(v)}>
                {WINDOWS.map((w) => <ToggleButton key={w} value={w} sx={{ px: 1.25, py: 0.25, textTransform: 'none' }}>{w}</ToggleButton>)}
              </ToggleButtonGroup>
            </Stack>
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendData} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
                  <defs>
                    <linearGradient id="jccArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={ct.series(0)} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={ct.series(0)} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid {...gridProps(ct)} />
                  <XAxis dataKey="date" {...axisProps(ct)} tickFormatter={(v) => String(v).slice(5)} minTickGap={24} />
                  <YAxis {...axisProps(ct)} allowDecimals={false} width={34} />
                  <RTooltip {...tooltipStyle(ct)}
                    formatter={(val: any, name: any) => {
                      if (name === 'count') return [val, 'Entries'];
                      return [formatCurrency(Number(val)), name];
                    }}
                    labelFormatter={(l: any) => new Date(l).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    content={({ active, payload, label }: any) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload;
                      return (
                        <Box sx={{ ...tooltipStyle(ct).contentStyle, p: 1.25 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            {new Date(label).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>Entries: <b>{p.count}</b></Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: c.good }}>Debit: {formatCurrency(p.debit)}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: c.bad }}>Credit: {formatCurrency(p.credit)}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: c.sub }}>Net: {formatCurrency(p.net)}</Typography>
                        </Box>
                      );
                    }}
                  />
                  <Area type="monotone" dataKey="count" stroke={ct.series(0)} strokeWidth={2} fill="url(#jccArea)" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 2.5, height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Requires Attention</Typography>
              {exceptions && <Chip size="small" label={`${exceptions.summary.total} items`} sx={{ height: 22, fontWeight: 700, bgcolor: exceptions.summary.critical ? `${c.bad}1f` : `${c.warn}1f`, color: exceptions.summary.critical ? c.bad : c.warn }} />}
            </Stack>
            <Stack spacing={1} sx={{ maxHeight: 240, overflowY: 'auto' }}>
              {!exceptions ? <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}><CircularProgress size={20} /></Box>
                : exceptions.exceptions.length === 0
                  ? <Stack alignItems="center" spacing={1} sx={{ py: 4 }}><TaskAlt sx={{ fontSize: 30, color: c.good }} /><Typography variant="body2" sx={{ fontWeight: 600 }}>All clear</Typography><Typography variant="caption" color="text.secondary">No exceptions need attention.</Typography></Stack>
                  : exceptions.exceptions.map((ex) => {
                    const tint = ex.severity === 'critical' ? c.bad : c.warn;
                    return (
                      <Stack key={ex.id} direction="row" alignItems="center" spacing={1.25}
                        sx={{ p: 1.25, borderRadius: 2, border: line, cursor: 'pointer', '&:hover': { bgcolor: c.panel2 } }}
                        onClick={() => navigate(ex.route)}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tint, flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ex.title}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{ex.detail}</Typography>
                        </Box>
                        <Stack alignItems="flex-end">
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>{ex.count}</Typography>
                          {ex.amount > 0 && <Typography variant="caption" color="text.secondary">{formatIndianNumber(ex.amount)}</Typography>}
                        </Stack>
                        <ChevronRight sx={{ fontSize: 18, color: c.sub }} />
                      </Stack>
                    );
                  })}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* filters */}
      <Paper elevation={0} sx={{ ...panelSx, p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search #, reference or narration…" value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, flexGrow: { xs: 1, md: 0 } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: c.sub }} /></InputAdornment> }} />
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All statuses</MenuItem>
            {['DRAFT', 'SUBMITTED', 'PENDING_APPROVAL', 'APPROVED', 'POSTED', 'REJECTED', 'REVERSED'].map((s) => <MenuItem key={s} value={s}>{stateLabel(s)}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Type" value={journalType} onChange={(e) => setJournalType(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="">All types</MenuItem>
            {JOURNAL_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Source" value={sourceType} onChange={(e) => setSourceType(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All sources</MenuItem>
            {SOURCE_TYPES.map((s) => <MenuItem key={s} value={s}>{stateLabel(s)}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Account" value={accountId} onChange={(e) => setAccountId(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Any account</MenuItem>
            {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.account_code} — {a.account_name}</MenuItem>)}
          </TextField>
          <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={{ minWidth: 145 }} />
          <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} sx={{ minWidth: 145 }} />
          <TextField size="small" type="number" label="Min ₹" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} sx={{ width: 110 }} />
          <TextField size="small" type="number" label="Max ₹" value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} sx={{ width: 110 }} />
          {filtersActive && <Button size="small" onClick={clearFilters}>Clear</Button>}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="body2" color="text.secondary"><b>{displayRows.length}</b> {displayRows.length === 1 ? 'entry' : 'entries'}</Typography>
        </Box>
        {/* saved views / quick filters */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Chip size="small" label="Attention" icon={<WarningAmberRounded sx={{ fontSize: 15 }} />} clickable
            variant={attentionOnly ? 'filled' : 'outlined'} color={attentionOnly ? 'error' : 'default'}
            onClick={() => setAttentionOnly((v) => !v)} sx={{ borderColor: c.line }} />
          {[['Drafts', 'DRAFT'], ['Pending Approval', 'PENDING_APPROVAL'], ['Posted', 'POSTED']].map(([label, s]) => (
            <Chip key={s} size="small" label={label} clickable variant={status === s ? 'filled' : 'outlined'}
              color={status === s ? 'primary' : 'default'} onClick={() => { setAttentionOnly(false); setStatus(status === s ? '' : s); }}
              sx={{ borderColor: c.line }} />
          ))}
        </Stack>
      </Paper>

      {error && <Box sx={{ mb: 2 }}><ErrorState message={error} onRetry={loadList} /></Box>}

      {/* table */}
      <TableSurface>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>Journal #</TableCell>
                <TableCell sx={headerCellSx}>Date</TableCell>
                <TableCell sx={headerCellSx}>Type</TableCell>
                <TableCell sx={headerCellSx}>Source</TableCell>
                <TableCell sx={headerCellSx}>Description</TableCell>
                <TableCell sx={headerCellSx}>Company</TableCell>
                <TableCell sx={headerCellSx} align="right">Debit</TableCell>
                <TableCell sx={headerCellSx} align="right">Credit</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ border: 0, py: 6 }}><CircularProgress /></TableCell></TableRow>
              ) : displayRows.length === 0 ? (
                <TableRow><TableCell colSpan={10} sx={{ border: 0 }}>
                  <TableEmptyState title="No journal entries match these filters" hint={filtersActive ? 'Clear the filters to see the full journal.' : 'Record your first journal entry to get started.'} />
                </TableCell></TableRow>
              ) : paged.map((r) => {
                const dr = toNum(r.total_debit), cr = toNum(r.total_credit);
                const st = rowState(r);
                const balanced = isBalanced(dr, cr);
                const busy = busyId === r.id;
                return (
                  <TableRow key={r.id} hover sx={{ ...(bodyRowSx as any), cursor: 'pointer', ...(balanced ? {} : { bgcolor: `${c.bad}0d` }) }}
                    onClick={() => navigate(`/finance/journal/${r.id}`)}>
                    <TableCell>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: typeTint(r.journal_type, c.accent) }}>{r.entry_number}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{new Date(r.entry_date).toLocaleDateString('en-IN')}</Typography></TableCell>
                    <TableCell>{r.journal_type ? <TypePill label={r.journal_type} tint={typeTint(r.journal_type, c.accent)} /> : <Typography variant="caption" color="text.secondary">—</Typography>}</TableCell>
                    <TableCell>
                      {r.source_type && r.source_type !== 'MANUAL'
                        ? <Chip size="small" label={stateLabel(r.source_type)} icon={<OpenInNew sx={{ fontSize: '13px !important' }} />}
                            onClick={(e) => { e.stopPropagation(); navigate(`/finance/journal/${r.id}`); }}
                            sx={{ height: 22, fontSize: '0.6875rem', bgcolor: `${c.cyan}14`, color: c.cyan, '& .MuiChip-icon': { color: c.cyan } }} />
                        : <Typography variant="caption" color="text.secondary">Manual</Typography>}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</Typography>
                      {r.reference && <Typography variant="caption" color="text.secondary">Ref: {r.reference}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{r.company || '—'}{r.plant ? ` · ${r.plant}` : ''}</Typography></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(dr)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(cr)}</TableCell>
                    <TableCell><StatusDot label={stateLabel(st)} tone={STATE_TONE[st] || 'neutral'} /></TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {busy ? <CircularProgress size={16} /> : (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {st === 'DRAFT' && <>
                            <Tooltip title="Submit for approval"><IconButton size="small" onClick={() => rowAction(() => journalWorkspaceService.submit(r.id), r.id, 'Submitted')}><Send sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                            <Tooltip title="Post"><IconButton size="small" onClick={() => rowAction(() => journalWorkspaceService.post(r.id), r.id, 'Posted')}><VerifiedUser sx={{ fontSize: 17, color: c.good }} /></IconButton></Tooltip>
                          </>}
                          {(st === 'SUBMITTED' || st === 'PENDING_APPROVAL') &&
                            <Tooltip title="Approve"><IconButton size="small" onClick={() => rowAction(() => journalWorkspaceService.approve(r.id), r.id, 'Approved')}><ThumbUp sx={{ fontSize: 17, color: c.accent }} /></IconButton></Tooltip>}
                          {st === 'APPROVED' &&
                            <Tooltip title="Post"><IconButton size="small" onClick={() => rowAction(() => journalWorkspaceService.post(r.id), r.id, 'Posted')}><VerifiedUser sx={{ fontSize: 17, color: c.good }} /></IconButton></Tooltip>}
                          {st === 'POSTED' &&
                            <Tooltip title="Reverse"><IconButton size="small" onClick={() => rowAction(() => journalWorkspaceService.reverse(r.id), r.id, 'Reversed with counter-entry')}><Undo sx={{ fontSize: 17, color: c.warn }} /></IconButton></Tooltip>}
                          <Tooltip title="Open 360"><IconButton size="small" onClick={() => navigate(`/finance/journal/${r.id}`)}><ArrowForward sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {displayRows.length > 0 && (
          <TablePagination component="div" count={displayRows.length} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50, 100]} />
        )}
      </TableSurface>

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

export default JournalControlCenter;
